import React from 'react';
import { observer } from 'mobx-react';

import { PropTypes } from 'prop-types';
import { DownloadLink } from '../link/download-link';
import { utilsService } from '../../core/services/utils.service';

@observer
export class Dropzone extends React.Component {

    static propTypes = {
        refName: PropTypes.string,
        saveRef: PropTypes.func.isRequired, // used for focusing
        disabled: PropTypes.bool,
        targetFile: PropTypes.object, // the fileMetaObject that contains all the info about the current upload
        onUpload: PropTypes.func,
        onDownload: PropTypes.func,
        onRemoveFile: PropTypes.func,
        uploadDate: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number
        ]),
        isRequired: PropTypes.bool,
        isValid: PropTypes.bool,
        label: PropTypes.string,
        helpText: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.object
        ]),
        errorText: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.object
        ]),
        hasError: PropTypes.bool
    }

    static defaultProps = {
        refName: 'primaryButtonRef',
        targetFile: {},
        uploadDate: null,
        disabled: false,
        hasError: false,
        isRequired: true,
        isValid: true,
        handleUpload: null,
    }

    constructor(props) {
        super(props);
        this.state = {
            isDraggedOver: false
        };
    }

    componentDidMount() {
        // Reset any errors on a new mount, just in case.
        this.props.targetFile.invalidUploadAttempted = false
    }

    get hasDownload () {
        return typeof this.props.onDownload === 'function';
    }

    get dropzoneClass() {
        let className = '';
        if (this.props.hasError) {
            className += 'has-error';
        }
        if (this.props.targetFile.filename) {
            className += ' uploaded';
            return className;
        } else if (this.state.isDraggedOver) {
            className += ' is-drag-over';
            return className;
        }
        return className;
    }

    onDragEnter = (event) => {
        event.preventDefault();
        this.setState({
            isDraggedOver: true
        });
    }

    onDragLeave = (event) => {
        event.preventDefault();
        this.setState({
            isDraggedOver: false
        });
    }

    handleUpload = (event) => {
        event.preventDefault();
        //if the upload was triggered during a drag event, remove the isDragged Over
        if (event.target.type !== 'file') {
            this.setState({
                isDraggedOver: false
            });
        }
        this.props.onUpload(event);
        this.primaryButtonRef.focus();
    }

    handleDownload = () => {
        if (this.hasDownload) {
            return this.props.onDownload();
        }
    }

    handleRemoveFile = (event) => {
        this.props.onRemoveFile(event);
        this.primaryButtonRef.focus();
    }

    handlePrimaryButtonClick = () => {
        if (!this.props.disabled) {
            this.fileBrowseInputEl.click();
        }
    }

    setPrimaryButtonRef = (ref) => {
        this.primaryButtonRef = ref;
        this.props.saveRef(this.props.refName, ref);
    }

    renderLabelSection = () => {
        return (
            <React.Fragment>
                <span className="sr-only">File upload area:</span>
                <span className={this.props.isRequired ? 'required' : ''}>{this.props.label}</span>
                <div className="help-text">
                    {this.props.helpText}
                </div>
            </React.Fragment>
        )
    }


    renderErrorSection = (errorText, announceImmediately = false) => {
        return (<div className="msgBlock error error-list">
            <span role={announceImmediately ? 'status' : ''} aria-live={announceImmediately ? 'assertive' : ''} aria-atomic="true">
                {errorText}
            </span>
        </div>)
    }


    renderStatusBar = () => {
        return (
            <React.Fragment>
                {this.props.targetFile.isUploading &&
                    <div className="progress loading-indicator">
                        <div className="progress-bar progress-bar-striped active" role="progressbar" aria-valuenow={this.props.targetFile.progress} aria-valuemin="0" aria-valuemax="100" style={{ width: `${this.props.targetFile.progress}%` }}>
                            <span className="sr-only">{this.props.targetFile.progress}% Complete</span>
                        </div>
                    </div>
                }
                {this.props.targetFile.isDeleting &&
                    <div className="progress loading-indicator">
                        <span className="sr-only">Deleting certifications file</span>
                        <div className="progress-bar progress-bar-striped progress-bar-danger active" role="progressbar" style={{ width: "100%" }}></div>
                    </div>
                }
            </React.Fragment>
        );
    }

    renderFileName = () => {
        const displayName = this.props.targetFile.filename;
        const timestamp = utilsService.normalizedDate(this.props.uploadDate, 'MMMM Do YYYY | h:mm:ss a')
        return (
            <React.Fragment>
                <div className="dropzone-icon"><i aria-hidden className="icon icon-file dkgrey"></i></div>
                {this.hasDownload &&
                    <div className="dropzone-file-info">
                        <DownloadLink
                            title={displayName}
                            onClick={this.handleDownload}
                            srText="Download current file"
                            smallText={this.props.targetFile.filename ? `Uploaded ${timestamp}` : 'Uploading...'}
                            deaden={this.props.targetFile.isUploading || this.props.targetFile.isDeleting} />
                    </div>
                }
                {!this.hasDownload &&
                    <div className="dropzone-file-info">
                        <div className="file-name">
                            {displayName}
                        </div>
                        <small>{this.props.targetFile.filename ? `Uploaded ${timestamp}` : 'Uploading...'}</small>
                    </div>
                }
            </React.Fragment>
        )
    }

    renderDragAndDropInstructions = () => {
        return (
            <React.Fragment>
                <div><i aria-hidden className="icon icon-upload dkgrey"></i></div>
                <p><strong>Drag and Drop File Here</strong></p>
                <p>or</p>
            </React.Fragment>
        );
    }

    renderInstructionArea = () => {
        const displayName = this.props.targetFile.filename;
        return (
            <div className="dropzone-content">
                {displayName &&
                    this.renderFileName()
                }
                {!displayName && !this.props.targetFile.isDeleting &&
                    this.renderDragAndDropInstructions()
                }
            </div>
        )
    }


    renderInputAndButton = () => {
        const containsFile = Boolean(this.props.targetFile.filename);
        const onClick = containsFile
            ? this.handleRemoveFile
            : this.handlePrimaryButtonClick;
        const isDisabled = this.props.targetFile.isUploading || this.props.targetFile.isDeleting || this.props.disabled;
        const buttonLabel = containsFile
            ? (<span>Remove <span className="sr-only">previously uploaded</span> File</span>)
            : 'Browse Files';
        return (<div className="file-input">
            <button aria-labelledby={!containsFile ? 'dropzone-label' : ''} ref={this.setPrimaryButtonRef} onClick={onClick} disabled={isDisabled} className="fn-primary">
                {buttonLabel}
                {!containsFile && <input ref={(i) => { this.fileBrowseInputEl = i }} aria-labelledby="dropzone-label" type="file" onChange={this.handleUpload} value="" />}
            </button>
        </div>);
    }

    render() {
        return (
            <div className="dropzone-wrapper">
                <div id="dropzone-label" className={`col-form-label ${this.props.hasError || !this.props.isValid ? 'error' : ''}`}>
                    {this.props.label && this.renderLabelSection()}
                    {this.renderErrorSection(this.props.errorText, !this.props.isValid)}
                </div>
                <div className={`dropzone ${this.dropzoneClass}`}
                    onDragLeave={this.onDragLeave}
                    onDragEnter={this.onDragEnter}
                    onDrop={this.handleUpload}
                    onDragOver={(event) => event.preventDefault()}>
                    {(this.props.targetFile.isUploading || this.props.targetFile.isDeleting) &&
                        this.renderStatusBar()
                    }
                    {this.renderInstructionArea()}
                    {this.renderInputAndButton()}
                </div>
            </div>
        );
    }
}
